"use client";

import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMapSearchParams } from "modules/common/components/map/hooks/useMapSearchParams";
import { useToast } from "modules/common/components/toast/useToast";
import { reverseGeocode } from "modules/server/fetch/geocoding";
import { clientLogger } from "modules/common/logger/client";

// Default coordinates (Sofia, Bulgaria)
export const SOFIA_LAT = 42.69751;
export const SOFIA_LNG = 23.32415;

const useUserLocation = () => {
  const [locationAccess, setLocationAccess] = useState(false);
  const {
    coords,
    city: urlCity,
    setLocation: setUrlLocation,
  } = useMapSearchParams();
  const [userLocation, setUserLocation] = useState<[number, number] | null>(
    null
  );
  const [isLoadingUserLocation, setIsLoadingUserLocation] = useState(false);
  const [{ latitude, longitude }, setLocation] = useState<
    Record<"latitude" | "longitude", number>
  >({ latitude: SOFIA_LAT, longitude: SOFIA_LNG });

  const toast = useToast();

  // Fetch city name via server action
  const fetchUserCity = useCallback(async () => {
    if (latitude === 0 || longitude === 0) return null;
    return reverseGeocode(latitude, longitude);
  }, [latitude, longitude]);

  const { isLoading, isError, data } = useQuery({
    queryKey: ["reverseGeocode", { lat: latitude, lng: longitude }],
    queryFn: fetchUserCity,
    enabled: latitude !== 0 && longitude !== 0 && !urlCity,
  });
  // Check if user has granted location access
  const checkLocationAccess = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.permissions) return;

    navigator.permissions
      .query({ name: "geolocation" })
      .then((permissionStatus) => {
        if (permissionStatus.state === "granted") {
          setLocationAccess(true);
        } else if (permissionStatus.state === "denied") {
          toast.error(
            "Location access denied. Please change your browser settings to allow location access."
          );
        }
      });
  }, [toast]);

  const triggerLocationFetch = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;

    setIsLoadingUserLocation(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const { latitude, longitude } = coords;
        setLocation({ latitude, longitude });
        setUserLocation([latitude, longitude]);
        // Update URL params with geolocation coordinates
        setUrlLocation([longitude, latitude]);
        setLocationAccess(true);
        setIsLoadingUserLocation(false);
      },
      (error) => {
        clientLogger.error("useUserLocation", "geolocation request failed", error);
        checkLocationAccess();

        setIsLoadingUserLocation(false);
      }
    );
  }, [checkLocationAccess, setUrlLocation]);

  // Retrieve latitude & longitude coordinates from `navigator.geolocation` Web API
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;

    if (locationAccess) {
      navigator.geolocation.getCurrentPosition(({ coords }) => {
        const { latitude, longitude } = coords;
        setLocation({ latitude, longitude });
      });
    }
  }, [locationAccess]);

  // Set search coords from user location data if user has granted location access
  useEffect(() => {
    if (locationAccess && !isLoading && !isError && data?.cityName) {
      // Update URL params with coordinates
      setUrlLocation([longitude, latitude]);
    }
  }, [
    locationAccess,
    data,
    isError,
    isLoading,
    latitude,
    longitude,
    setUrlLocation,
  ]);

  // Derive city name from URL params first (source of truth), then reverse geocoding data (fallback)
  const searchCity = urlCity || data?.cityName;

  return {
    searchCoords: coords,
    searchCity,
    urlCity,
    userLocation,
    isLoading,
    triggerLocationFetch,
    isLoadingUserLocation,
  };
};

export default useUserLocation;
